import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { fetchWelfareActivities } from '../../services/api'

const FALLBACK_SHOWCASE_IMAGES = [
  { img: '/gallery/1.png', label: 'EMERGENCY RESCUE' },
  { img: '/streetdog1.png', label: 'TRAUMA CARE' },
  { img: '/gallery/4.png', label: 'MASS VACCINATION' },
  { img: '/volunteer/v2.png', label: 'RABIES PREVENTION' },
  { img: '/gallery/3.png', label: 'HEALTH DRIVES' },
  { img: '/gallery/2.png', label: 'DAILY FEEDING' },
  { img: '/volunteer/v1.png', label: 'BIRTH CONTROL (ABC)' },
  { img: '/volunteer/v3.png', label: 'COMMUNITY OUTREACH' },
]

const KEYWORDS = [
  'RAPID RESCUE',
  'TRAUMA CARE',
  'MASS VACCINATION',
  'DAILY FEEDING',
  'HUMANE BIRTH CONTROL',
  'ANTI-RABIES CAMPS',
  'MOBILE TRIAGE',
  '24/7 AMBULANCE UNIT',
]

const WelfareShowcase = () => {
  const navigate = useNavigate()
  const [showcaseImages, setShowcaseImages] = useState(FALLBACK_SHOWCASE_IMAGES)

  useEffect(() => {
    fetchWelfareActivities().then((data) => {
      if (data && data.length > 0) {
        const extracted = []
        data.forEach((act) => {
          const imgList = Array.isArray(act.images) && act.images.length > 0
            ? act.images
            : (act.image_url ? [act.image_url] : [])

          imgList.forEach((img) => {
            if (img && typeof img === 'string' && img.trim()) {
              extracted.push({
                img: img.trim(),
                label: act.badge || act.title || 'WELFARE INITIATIVE',
              })
            }
          })
        })

        if (extracted.length > 0) {
          setShowcaseImages(extracted)
        }
      }
    }).catch((err) => {
      console.warn('Could not fetch welfare activities for showcase:', err)
    })
  }, [])

  const handleNavigate = () => {
    navigate('/welfare-activities')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const bgItems = showcaseImages.length < 6
    ? [...showcaseImages, ...FALLBACK_SHOWCASE_IMAGES]
    : showcaseImages

  return (
    <section className="w-full px-0 sm:px-6 lg:px-8 2xl:px-12 py-0 sm:py-6 lg:py-8 select-none">
      <div className="relative w-full max-w-8xl mx-auto bg-gradient-to-r from-brand-orange-600 via-brand-orange-500 to-brand-orange-600 rounded-none sm:rounded-[40px] lg:rounded-[48px] pt-8 xs:pt-10 pb-44 xs:pb-52 sm:py-16 lg:py-20 2xl:py-24 px-4 xs:px-6 sm:px-8 lg:px-12 overflow-hidden text-white shadow-none sm:shadow-xl flex items-center justify-center">
        {/* Ambient Radial Highlights */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-black/10 rounded-full blur-3xl pointer-events-none" />

        {/* Dynamic Keyframe Animation for Single Big Background Stream - Ultra Slow & Smooth */}
        <style>{`
          @keyframes welfare-big-glide {
            0% { transform: translate3d(0, 0, 0); }
            100% { transform: translate3d(-50%, 0, 0); }
          }
          .animate-welfare-big-stream {
            display: flex;
            width: max-content;
            animation: welfare-big-glide 180s linear infinite;
          }
        `}</style>

        {/* ============================================================== */}
        {/* BACKGROUND: DYNAMIC HEIGHT IMAGE-ONLY LOW-OPACITY STREAM */}
        {/* ============================================================== */}
        <div className="absolute inset-0 flex items-stretch pointer-events-none overflow-hidden opacity-[0.06] sm:opacity-[0.12] z-0 grayscale contrast-150 p-2 sm:p-4">
          <div className="animate-welfare-big-stream flex items-stretch h-full gap-4 sm:gap-8">
            {/* First Set */}
            {bgItems.map((item, idx) => (
              <div
                key={`big-img-1-${idx}`}
                className="h-full aspect-[4/3] sm:aspect-[16/10] rounded-2xl sm:rounded-3xl overflow-hidden border border-white/30 sm:border-2 sm:border-white/40 shrink-0 shadow-xl"
              >
                <img src={item.img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
            {/* Loop Duplicate */}
            {bgItems.map((item, idx) => (
              <div
                key={`big-img-2-${idx}`}
                className="h-full aspect-[4/3] sm:aspect-[16/10] rounded-2xl sm:rounded-3xl overflow-hidden border border-white/30 sm:border-2 sm:border-white/40 shrink-0 shadow-xl"
              >
                <img src={item.img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* ============================================================== */}
        {/* FOREGROUND: CLEAN EDITORIAL MANIFESTO STATEMENT (TOP-LEFT) */}
        {/* ============================================================== */}
        <div className="w-full relative z-10 text-left">
          {/* Top Label */}
          <div className="flex items-center gap-2.5 sm:gap-3 mb-2.5 sm:mb-4 justify-start">
            <span className="text-[10px] xs:text-xs sm:text-[11px] 2xl:text-xs font-extrabold tracking-widest text-white/85 uppercase">
              OUR ON-GROUND WELFARE COMMITMENT
            </span>
            <div className="h-[1.5px] w-6 sm:w-12 2xl:w-16 bg-white/60 shrink-0" />
          </div>

          {/* Welfare Statement Heading - Full width on mobile */}
          <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-3.5xl lg:text-[38px] xl:text-[42px] 2xl:text-[48px] text-white font-medium leading-[1.3] sm:leading-[1.38] tracking-tight w-full max-w-full lg:max-w-2xl xl:max-w-3xl text-left">
            Through{' '}
            <span className="text-white/75 font-light">24/7 trauma rescues</span>,{' '}
            mass <span className="text-white/75 font-light">anti-rabies vaccinations</span>,{' '}
            and <span className="text-white/75 font-light">daily street feeding</span>,{' '}
            <span className="font-normal text-white drop-shadow-sm" style={{ fontFamily: 'var(--app-font-heading)' }}>
              CAWT
            </span>{' '}
            works tirelessly on the ground to ensure{' '}
            <span className="text-white/75 font-light">no innocent animal suffers in silence</span>.
          </h2>

          {/* Action Button */}
          <div className="mt-4 sm:mt-7 md:mt-8 text-left">
            <Link
              to="/welfare-activities"
              onClick={handleNavigate}
              className="group relative inline-flex items-center gap-1.5 sm:gap-3 px-3.5 py-1.5 xs:px-4 xs:py-2 sm:px-6 sm:py-3.5 bg-white hover:bg-slate-50 text-brand-orange-700 font-extrabold text-[10px] xs:text-[11px] sm:text-sm tracking-wider uppercase shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer rounded-full border border-white hover:scale-105 active:scale-95"
            >
              <span className="inline sm:hidden">Welfare Activities</span>
              <span className="hidden sm:inline">See Welfare Activities &amp; Initiatives</span>
              <div className="w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-brand-orange-100 flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:translate-x-1">
                <svg className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-brand-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </Link>
          </div>
        </div>

        {/* ============================================================== */}
        {/* RIGHT-SIDE BOTTOM: ENLARGED TRANSPARENT DOG ANIMATION (WEBP) */}
        {/* ============================================================== */}
        <div className="block absolute bottom-0 right-0 xs:right-1 sm:right-4 lg:right-6 xl:right-10 2xl:right-16 pointer-events-none z-20 w-[240px] xs:w-[280px] sm:w-[320px] lg:w-[380px] xl:w-[440px] 2xl:w-[500px] ">
          <video
            src="/stray.webm"
            width="500"
            height="281"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
            className="w-full h-full block object-contain object-bottom translate-y-1 pointer-events-none"
          >
            <track kind="captions" src="data:text/vtt,WEBVTT" label="No audio" />
          </video>
        </div>
      </div>
    </section>
  )
}

export default WelfareShowcase
